import { useTranslation } from "react-i18next";
import { Sprout } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { formatNumber } from "../../../../utils";
import { formatAdminAreaChain } from "../../../../utils/adminArea";
import { StockTraceability } from "../types";
import { formatDate, formatDateTime } from "../utils";
import { DetailItem, DetailList } from "./DetailList";

const Section = ({ title, items }: { title: string; items: DetailItem[] }) => (
  <div className="flex flex-col gap-1">
    <h3 className="font-semibold text-foreground text-sm">{title}</h3>
    <DetailList items={items} />
  </div>
);

export const StockContext = ({ traceability }: { traceability: StockTraceability }) => {
  const { t } = useTranslation();
  const { farmer_organization, collected_by, farmer, production, collection } = traceability;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sprout className="size-4 text-primary" />
          {t("farmersMarket.context.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-2">
        <Section
          title={t("farmersMarket.context.origin")}
          items={[
            {
              label: t("farmersMarket.context.farmerOrganization"),
              value: farmer_organization.name,
            },
            {
              label: t("farmersMarket.context.location"),
              value: formatAdminAreaChain(farmer_organization.location) || "–",
            },
            ...(collected_by
              ? [{ label: t("farmersMarket.context.collectedBy"), value: collected_by.name ?? "–" }]
              : []),
            ...(farmer
              ? [
                  { label: t("farmersMarket.context.farmer"), value: farmer.name },
                  {
                    label: t("farmersMarket.context.farmerLocation"),
                    value:
                      [formatAdminAreaChain(farmer.location), farmer.location_details]
                        .filter(Boolean)
                        .join(" · ") || "–",
                  },
                ]
              : []),
          ]}
        />
        <Section
          title={t("farmersMarket.context.collection")}
          items={[
            {
              label: t("farmersMarket.context.collectedAt"),
              value: formatDateTime(collection.collected_at),
            },
            {
              label: t("farmersMarket.context.listedAt"),
              value: formatDateTime(collection.listed_at),
            },
            {
              label: t("farmersMarket.context.harvestDate"),
              value: formatDate(collection.harvest_date),
            },
            {
              label: t("farmersMarket.context.collectionLocation"),
              value: formatAdminAreaChain(collection.location) || "–",
            },
            {
              label: t("farmersMarket.context.qualityGrade"),
              value: collection.quality_grade?.name ?? "–",
            },
          ]}
        />
        {production && (
          <Section
            title={t("farmersMarket.context.production")}
            items={[
              { label: t("farmersMarket.context.crop"), value: production.commodity?.name ?? "–" },
              {
                label: t("farmersMarket.context.farmSize"),
                value: production.land_size
                  ? `${formatNumber(production.land_size)} ${production.land_unit ?? ""}`
                  : "–",
              },
              ...(production.farmland_identifier
                ? [
                    {
                      label: t("farmersMarket.context.farmland"),
                      value: production.farmland_identifier,
                    },
                  ]
                : []),
              {
                label: t("farmersMarket.context.estimatedYield"),
                value:
                  production.estimated_yield !== null
                    ? `${formatNumber(production.estimated_yield)} ${production.estimated_yield_unit ?? ""}`
                    : "–",
              },
              {
                label: t("farmersMarket.context.startDate"),
                value: formatDate(production.start_date),
              },
            ]}
          />
        )}
        {production && production.harvests.length > 0 && (
          <Section
            title={t("farmersMarket.context.harvests")}
            items={production.harvests.map(harvest => ({
              label: formatDate(harvest.date),
              value: [formatNumber(harvest.quantity), harvest.details].filter(Boolean).join(" · "),
            }))}
          />
        )}
      </CardContent>
    </Card>
  );
};
